<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :show-close="true"
    :close-on-click-modal="true"
    :modal-append-to-body="true"
    custom-class="login-dialog"
    width="480px"
    @close="handleClose"
    @open="onOpen"
  >
    <div class="dialog-header">
      <h3 class="dialog-title">{{ $t('common.login') }}</h3>
    </div>
    <el-form ref="loginForm" :model="form" :rules="rules" class="login-form">
      <el-form-item prop="email">
        <el-input v-model="form.email" :placeholder="$t('login.emailPlaceholder')" autocomplete="off"></el-input>
      </el-form-item>
      <el-form-item prop="password">
        <el-input v-model="form.password" type="password" :placeholder="$t('login.passwordPlaceholder')" autocomplete="new-password"></el-input>
      </el-form-item>
      <el-form-item prop="captcha">
        <div class="captcha-row">
          <el-input v-model="form.captcha" :placeholder="$t('login.captchaPlaceholder')" autocomplete="off"></el-input>
          <div class="captcha-img" @click="refreshCaptcha">
            <img v-if="captchaUrl" :src="captchaUrl" alt="captcha" />
            <span v-else class="captcha-placeholder"></span>
          </div>
        </div>
      </el-form-item>
      <div class="form-extra">
        <span class="forgot-link" @click="$emit('forgot')">{{ $t('login.forgot') }}</span>
      </div>
      <el-form-item>
        <el-button type="primary" class="submit-btn" round @click="handleLogin">{{ $t('common.login') }}</el-button>
      </el-form-item>
    </el-form>
    <div class="dialog-footer">
      <span class="register-link" @click="goRegister">{{ $t('login.goRegister') }}</span>
    </div>
  </el-dialog>
</template>

<script>
import debounce from 'lodash/debounce'
import { getCaptchaImage, login, getInfo } from '@/api/base'
import { setToken } from '@/utils/auth'

export default {
  name: 'LoginDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        email: '',
        password: '',
        captcha: '',
        uuid: ''
      },
      captchaUrl: ''
    }
  },
  computed: {
    rules() {
      return {
        email: [
          { required: true, message: this.$t('validation.emailRequired'), trigger: 'blur' },
          {
            pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            message: this.$t('validation.emailFormat'),
            trigger: 'blur'
          }
        ],
        password: [
          { required: true, message: this.$t('validation.passwordRequired'), trigger: 'blur' }
        ],
        captcha: [
          { required: true, message: this.$t('validation.captchaRequired'), trigger: 'blur' }
        ]
      }
    },
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  created() {
    this.handleLogin = debounce(this.submitLogin, 500, {
      leading: true,
      trailing: false
    })
  },
  methods: {
    onOpen() {
      this.resetForm()
      this.refreshCaptcha()
    },
    resetForm() {
      this.form = {
        email: '',
        password: '',
        captcha: '',
        uuid: ''
      }
      this.captchaUrl = ''
      this.$nextTick(() => {
        this.$refs.loginForm && this.$refs.loginForm.clearValidate()
      })
    },
    handleClose() {
      if (this.$refs.loginForm) {
        this.$refs.loginForm.clearValidate()
      }
      this.$emit('update:visible', false)
    },
    submitLogin() {
      this.$refs.loginForm.validate((valid) => {
        if (!valid) return
        login({
          email: this.form.email,
          password: this.form.password,
          code: this.form.captcha,
          uuid: this.form.uuid
        }).then(res => {
          const token = res.data
          setToken(token)
          return getInfo().then(userRes => {
            this.$store.dispatch('login', { user: userRes.data, token })
            this.$emit('update:visible', false)
          })
        }).catch(err => {})
      })
    },
    goRegister() {
      this.$emit('update:visible', false)
      this.$emit('go-register')
    },
    refreshCaptcha() {
      getCaptchaImage().then(res => {
        this.captchaUrl = 'data:image/gif;base64,' + res.data.img
        this.form.uuid = res.data.uuid
      }).catch(err => {})
    }
  },
  beforeDestroy() {
    if (this.handleLogin && typeof this.handleLogin.cancel === 'function') {
      this.handleLogin.cancel()
    }
  }
}
</script>

<style lang="scss" scoped>
.dialog-header {
  text-align: center;
  margin-bottom: 30px;

  .dialog-title {
    font-size: 20px;
    font-weight: 600;
    color: #0866FF;
  }
}

.login-form {
  padding: 0 20px;

  ::v-deep .el-input__inner {
    border: none;
    border-radius: 25px;
    background: #F8F8F8;
    padding-left: 16px;
    font-size: 14px;
    height: 44px;

    &:focus {
      border: none;
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px #F8F8F8 inset;
      transition: background-color 5000s ease-in-out 0s;
    }
  }
}

.captcha-row {
  display: flex;
  align-items: center;
  gap: 12px;

  .el-input {
    flex: 1;
  }

  .captcha-img {
    // width: 120px;
    height: 38px;
    cursor: pointer;
    background: #ccc;
    // border-radius: 4px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}

.form-extra {
  text-align: right;
  margin-bottom: 20px;

  .forgot-link {
    font-size: 13px;
    color: #BABABA;
    cursor: pointer;

    &:hover {
      color: #0866FF;
    }
  }
}

.submit-btn {
  width: 100%;
  background: #0866FF;
  border-color: #0866FF;
  font-size: 16px;
  padding: 12px 0;
}

.dialog-footer {
  text-align: center;
  margin-top: 16px;

  .register-link {
    font-size: 14px;
    color: #BABABA;
    cursor: pointer;

    &:hover {
      color: #0866FF;
    }
  }
}
</style>

<style lang="scss">
.login-dialog {
  border-radius: 25px !important;
  padding: 30px 40px;

  .el-dialog__header {
    padding: 0;
    position: relative;
  }

  .el-dialog__headerbtn {
    top: 0;
    right: 0;
    font-size: 20px;
    display: block;
    position: absolute;
    z-index: 1;
  }

  .el-dialog__body {
    padding: 0;
  }
}
</style>
